import { useHttpClient } from '@/common/api/httpClient';
import type { StorageAreaResponse } from './models/storageAreaResponse';
import type { StorageAreaType } from 'shared/storageAreas/domain/storageAreaType';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import type { Unit } from 'shared/common/domain/bulkUnits';

export interface InventoryStorageArea {
  storageAreaId: string;
  storageAreaType: StorageAreaType;
  logYardData?: InventoryLogYardData[];
  bulkStorageData?: InventoryBulkStorageData[];
  clientTimestampUtc?: string;
  undo?: boolean;
}

export interface InventoryLogYardData {
  productId: string;
  numberOfLogs: number;
  volume: number;
}

export interface InventoryBulkStorageData {
  productId: string;
  ton: number;
}

export interface InventoryStorageAreaResponse {
  ok: true;
  storageArea: StorageAreaResponse;
}

export function useInventoryStorageArea() {
  const httpClient = useHttpClient();
  const languageStore = useLanguageStore();
  const { translations } = storeToRefs(languageStore);

  return {
    execute,
  };

  async function execute(action: InventoryStorageArea) {
    if (!action.clientTimestampUtc) {
      action.clientTimestampUtc = new Date().toJSON();
    }

    const headers = httpClient.defaultHeaders();

    const request = new Request(httpClient.siteUrl('inventory'), {
      headers,
      method: 'POST',
      body: JSON.stringify(action),
    });

    const text = translations.value.storageAreas.actions.inventory;

    const response = await httpClient.action<InventoryStorageAreaResponse>(request, {
      title: text.title,
      errorTitle: text.error,
      async processOk(response) {
        const json = (await response.json()) as StorageAreaResponse;

        return {
          ok: true,
          storageArea: json,
        };
      },
    });

    return response;
  }
}
